import { useEffect, useState } from 'react';
import type {
  PublisherFilterState,
  PublisherSavedView,
  PublisherSavedViewsStore,
} from '../../../types/managePublishers';
import { useTranslation } from '../../../i18n/useTranslation';

const STORAGE_KEY = 'learnleaf:manage-publishers:saved-views:v1';

function loadStore(): PublisherSavedViewsStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: 1, views: [] };
    const parsed = JSON.parse(raw) as PublisherSavedViewsStore;
    if (parsed.version !== 1 || !Array.isArray(parsed.views)) return { version: 1, views: [] };
    return parsed;
  } catch {
    return { version: 1, views: [] };
  }
}

function saveStore(s: PublisherSavedViewsStore) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

interface Props {
  filters: PublisherFilterState;
  onApply: (filters: PublisherFilterState) => void;
}

export default function PublisherSavedViews({ filters, onApply }: Props) {
  const { t } = useTranslation();
  const [store, setStore] = useState<PublisherSavedViewsStore>({ version: 1, views: [] });
  const [name, setName] = useState('');

  useEffect(() => { setStore(loadStore()); }, []);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const next: PublisherSavedView = {
      id: crypto.randomUUID(),
      name: trimmed,
      createdAt: new Date().toISOString(),
      filters,
    };
    const updated: PublisherSavedViewsStore = { version: 1, views: [...store.views, next] };
    saveStore(updated);
    setStore(updated);
    setName('');
  };

  const handleDelete = (id: string) => {
    const updated: PublisherSavedViewsStore = { version: 1, views: store.views.filter(v => v.id !== id) };
    saveStore(updated);
    setStore(updated);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
      <span className="text-neutral-600">{t('App.Admin.Publishers.PublisherSavedViews.Label')}</span>
      {store.views.length === 0 && <span className="text-neutral-400">{t('App.Admin.Publishers.PublisherSavedViews.None')}</span>}
      {store.views.map(v => (
        <span key={v.id} className="inline-flex items-center border border-neutral-300 rounded">
          <button
            type="button"
            onClick={() => onApply(v.filters)}
            className="px-2 py-1 hover:bg-neutral-100"
          >
            {v.name}
          </button>
          <button
            type="button"
            onClick={() => handleDelete(v.id)}
            className="px-1 py-1 text-neutral-400 hover:text-red-600"
            aria-label={t('App.Admin.Publishers.PublisherSavedViews.DeleteAriaLabel', { name: v.name })}
          >
            ×
          </button>
        </span>
      ))}
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('App.Admin.Publishers.PublisherSavedViews.SavePlaceholder')}
        className="border border-neutral-300 rounded px-2 py-1 text-sm w-44"
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={!name.trim()}
        className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50"
      >
        {t('App.Admin.Publishers.PublisherSavedViews.SaveButton')}
      </button>
    </div>
  );
}
